/** Alineado con POST /api/ingest/upload (#0000039). */
export const INGEST_UPLOAD_MAX_FILE_BYTES = 1024 * 1024 * 1024;

export const INGEST_UPLOAD_ALLOWED_EXTENSIONS = new Set([
  'pdf',
  'tif',
  'tiff',
  'jpg',
  'jpeg',
  'png',
  'doc',
  'docx',
  'txt',
  'json',
  'iso',
]);

export const INGEST_UPLOAD_ACCEPT_EXTENSIONS =
  '.pdf,.tif,.tiff,.jpg,.jpeg,.png,.doc,.docx,.txt,.json,.iso';

export const INGEST_UPLOAD_HINT_TYPES =
  'PDF, TIFF, JPG, PNG, Word (.doc, .docx), TXT, JSON e ISO';

export function ingestUploadFileExtension(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) {
    return '';
  }
  return base.slice(dot + 1).toLowerCase();
}

export function isIngestUploadExtensionAllowed(extension: string): boolean {
  return INGEST_UPLOAD_ALLOWED_EXTENSIONS.has(extension.trim().toLowerCase());
}

export function formatIngestUploadMaxSizeLabel(maxBytes: number): string {
  const gib = 1024 * 1024 * 1024;
  if (maxBytes >= gib && maxBytes % gib === 0) {
    return `${maxBytes / gib} GB`;
  }
  const mib = 1024 * 1024;
  return `${Math.round(maxBytes / mib)} MB`;
}

export type IngestUploadFileRejectReason = 'invalid_format' | 'size_exceeded';

export interface IngestUploadFileValidationIssue {
  displayName: string;
  reason: IngestUploadFileRejectReason;
}

export function getIngestUploadFileValidationIssue(
  fileName: string,
  sizeBytes: number,
  maxBytes: number = INGEST_UPLOAD_MAX_FILE_BYTES,
): IngestUploadFileValidationIssue | null {
  const displayName = fileName.split(/[/\\]/).pop() ?? fileName;
  const extension = ingestUploadFileExtension(fileName);
  if (!extension || !isIngestUploadExtensionAllowed(extension)) {
    return { displayName, reason: 'invalid_format' };
  }
  if (sizeBytes > maxBytes) {
    return { displayName, reason: 'size_exceeded' };
  }
  return null;
}

const INGEST_UPLOAD_REJECTED_NAME_MAX_LENGTH = 56;

/** Acorta nombres largos conservando la extensión (p. ej. listados en modal). */
export function truncateIngestUploadDisplayName(
  name: string,
  maxLength: number = INGEST_UPLOAD_REJECTED_NAME_MAX_LENGTH,
): string {
  if (name.length <= maxLength) {
    return name;
  }
  const dot = name.lastIndexOf('.');
  const extension = dot > 0 ? name.slice(dot) : '';
  const baseMax = maxLength - extension.length - 1;
  if (baseMax < 10) {
    return `${name.slice(0, maxLength - 1)}…`;
  }
  return `${name.slice(0, baseMax)}…${extension}`;
}

function formatRejectedFileList(names: string[], maxItems = 6): string {
  if (names.length === 0) {
    return '';
  }
  const preview = names
    .slice(0, maxItems)
    .map((name) => `•\u00A0${truncateIngestUploadDisplayName(name)}`)
    .join('\n');
  if (names.length <= maxItems) {
    return preview;
  }
  return `${preview}\n•\u00A0… y ${names.length - maxItems} archivo(s) más.`;
}

/** Resumen agrupado para varios archivos rechazados (p. ej. carga por carpeta). */
export function formatIngestUploadRejectionSummary(
  issues: IngestUploadFileValidationIssue[],
): string {
  const invalidFormat = issues
    .filter((issue) => issue.reason === 'invalid_format')
    .map((issue) => issue.displayName);
  const sizeExceeded = issues
    .filter((issue) => issue.reason === 'size_exceeded')
    .map((issue) => issue.displayName);

  const parts: string[] = [];
  if (invalidFormat.length > 0) {
    parts.push(
      `${invalidFormat.length} archivo(s) con formato no permitido:\n\n${formatRejectedFileList(invalidFormat)}`,
    );
  }
  if (sizeExceeded.length > 0) {
    parts.push(
      `${sizeExceeded.length} archivo(s) superan ${formatIngestUploadMaxSizeLabel(INGEST_UPLOAD_MAX_FILE_BYTES)}:\n\n${formatRejectedFileList(sizeExceeded)}`,
    );
  }
  return parts.join('\n\n');
}

export function formatIngestUploadFolderRejectionAlert(
  issues: IngestUploadFileValidationIssue[],
  addedCount: number,
): { title: string; message: string } {
  const summary = formatIngestUploadRejectionSummary(issues);
  const rulesHint = `Permitidos: ${INGEST_UPLOAD_HINT_TYPES}. Máx. ${formatIngestUploadMaxSizeLabel(INGEST_UPLOAD_MAX_FILE_BYTES)} por archivo.`;

  if (addedCount > 0) {
    return {
      title: 'Algunos archivos omitidos',
      message: `Se añadieron ${addedCount} archivo(s) válido(s). ${issues.length} no se incluyeron.\n${summary}\n${rulesHint}`,
    };
  }

  return {
    title: 'Archivos no permitidos',
    message: `${summary}\n\n${rulesHint}`,
  };
}

/** @deprecated Usar formatIngestUploadFolderRejectionAlert */
export function formatIngestUploadFolderRejectionDialog(
  issues: IngestUploadFileValidationIssue[],
  addedCount: number,
): { title: string; message: string } {
  return formatIngestUploadFolderRejectionAlert(issues, addedCount);
}

/** null si el archivo es válido; mensaje legible para un solo archivo. */
export function validateIngestUploadFile(
  fileName: string,
  sizeBytes: number,
  maxBytes: number = INGEST_UPLOAD_MAX_FILE_BYTES,
): string | null {
  const issue = getIngestUploadFileValidationIssue(fileName, sizeBytes, maxBytes);
  if (!issue) {
    return null;
  }
  if (issue.reason === 'invalid_format') {
    return `«${issue.displayName}» no tiene un formato permitido. ${INGEST_UPLOAD_HINT_TYPES}.`;
  }
  return `«${issue.displayName}» supera el tamaño máximo (${formatIngestUploadMaxSizeLabel(maxBytes)}).`;
}
